import { useEffect, useState } from "react";
import { Page, Skeleton } from "../components/ui.jsx";

// Filed reports. Senior sees every one; a leader sees only those naming them
// as a reporter. The filtering is done by the API, not here — this screen
// cannot render anything the function declined to return.
//
// `senior` comes back from the API rather than being read off the session, so
// the acknowledge and resolve controls appear exactly when the server would
// accept them.

const when = (ts) =>
  ts
    ? new Date(ts).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const day = (iso) =>
  iso
    ? new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

const STATUS_LABEL = { open: "Open", acknowledged: "Acknowledged", resolved: "Resolved" };

function Detail({ id, onBack, onChanged, senior }) {
  const [incident, setIncident] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState(null);
  const [categories, setCategories] = useState([]);
  // The update box, and the people editor. Both are separate from `edit`
  // because neither touches the account of what happened.
  const [note, setNote] = useState("");
  const [people, setPeople] = useState([]);
  const [names, setNames] = useState(null);
  const [hunt, setHunt] = useState("");

  const load = () =>
    fetch("/api/incidents/" + encodeURIComponent(id))
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not open that report.");
        setIncident(d.incident);
      })
      .catch((e) => setError(String(e.message || e)));

  useEffect(() => {
    load();
    fetch("/api/incidents/categories")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCategories(d?.categories || []))
      .catch(() => {});
    // Every leader and above, so somebody can be added to the report after it
    // was filed. The server re-checks each key it is handed.
    fetch("/api/incidents/people")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setPeople(d?.people || []))
      .catch(() => {});
  }, [id]);

  const startEdit = () => {
    setError("");
    setEdit({
      occurredOn: incident.occurredOn,
      category: incident.category,
      affected: incident.affected,
      involved: incident.involved,
      description: incident.description,
      escalated: incident.escalated,
      escalatedTo: incident.escalatedTo,
      notEscalatedReason: incident.notEscalatedReason,
    });
  };

  const saveEdit = async () => {
    setBusy("edit");
    setError("");
    try {
      const r = await fetch(`/api/incidents/${encodeURIComponent(id)}/edit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(edit),
      });
      const d = await r.json();
      if (!r.ok) setError(d.error || "Could not save the edit.");
      else {
        setIncident(d.incident);
        setEdit(null);
        onChanged();
      }
    } catch {
      setError("Could not save the edit.");
    }
    setBusy("");
  };

  const addUpdate = async () => {
    if (note.trim().length < 2) return;
    setBusy("note");
    setError("");
    try {
      const r = await fetch(`/api/incidents/${encodeURIComponent(id)}/update`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text: note.trim() }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.error || "Could not add that update.");
      else {
        setIncident(d.incident);
        setNote("");
        onChanged();
      }
    } catch {
      setError("Could not add that update.");
    }
    setBusy("");
  };

  const saveNames = async () => {
    setBusy("names");
    setError("");
    try {
      const r = await fetch(`/api/incidents/${encodeURIComponent(id)}/reporters`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reporters: names }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.error || "Could not save that.");
      // Taking yourself off a report takes your access with it, so there is
      // nothing left here to show.
      else if (d.lostAccess) onBack();
      else {
        setIncident(d.incident);
        setNames(null);
        onChanged();
      }
    } catch {
      setError("Could not save that.");
    }
    setBusy("");
  };

  const stamp = async (which) => {
    setBusy(which);
    try {
      const r = await fetch(`/api/incidents/${encodeURIComponent(id)}/${which}`, {
        method: "POST",
      });
      const d = await r.json();
      if (r.ok) {
        setIncident(d.incident);
        onChanged();
      } else setError(d.error || "Could not save.");
    } catch {
      setError("Could not save.");
    }
    setBusy("");
  };

  // Only a failed *load* replaces the whole report. A failed save has to leave
  // the record on screen — swapping it for an error note would throw away what
  // the person had just typed into the panel above it.
  if (error && !incident) return <div className="empty-note">{error}</div>;
  if (!incident) return <Skeleton />;

  const i = incident;

  return (
    <div className="inc-detail">
      <div className="chips noprint" style={{ marginBottom: 14 }}>
        <button className="chip" onClick={onBack}>
          ← All reports
        </button>
        <button className="chip" onClick={() => window.print()}>
          🖨 Print / save as PDF
        </button>
        {!edit && (
          <button className="chip" onClick={startEdit}>
            ✎ Edit
          </button>
        )}
        {!names && (
          <button
            className="chip"
            onClick={() => {
              setError("");
              setNames((i.reporters || []).slice(1));
            }}
          >
            👥 People on this report
          </button>
        )}
      </div>

      {edit && (
        <div className="inc-editing">
          <div className="mdl-note">
            {i.acknowledged || i.resolved
              ? "This report has been signed off. Saving a change clears the acknowledgement, so it has to be given again against the new text. The original signing is kept in the history."
              : "Every change is recorded with your name, the time, and what it was before."}
          </div>

          <div className="mdl-row2">
            <label className="fld">
              <span className="flab">Date of the incident</span>
              <input
                className="finput"
                type="date"
                value={edit.occurredOn}
                onChange={(e) => setEdit({ ...edit, occurredOn: e.target.value })}
              />
            </label>
            <label className="fld">
              <span className="flab">What kind of incident</span>
              <select
                className="finput"
                value={edit.category}
                onChange={(e) => setEdit({ ...edit, category: e.target.value })}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mdl-row2">
            <label className="fld">
              <span className="flab">Who it happened to</span>
              <input
                className="finput"
                value={edit.affected}
                onChange={(e) => setEdit({ ...edit, affected: e.target.value })}
              />
            </label>
            <label className="fld">
              <span className="flab">Who it came from</span>
              <input
                className="finput"
                value={edit.involved}
                onChange={(e) => setEdit({ ...edit, involved: e.target.value })}
              />
            </label>
          </div>

          <label className="fld">
            <span className="flab">What happened</span>
            <textarea
              className="finput ftall"
              value={edit.description}
              onChange={(e) => setEdit({ ...edit, description: e.target.value })}
            />
          </label>

          <div className="fld">
            <span className="flab">Escalated to an authority outside the church?</span>
            <div className="chips" style={{ marginTop: 8 }}>
              <button
                className={"chip" + (edit.escalated ? " on" : "")}
                onClick={() => setEdit({ ...edit, escalated: true })}
              >
                Yes
              </button>
              <button
                className={"chip" + (!edit.escalated ? " on" : "")}
                onClick={() => setEdit({ ...edit, escalated: false })}
              >
                No
              </button>
            </div>
          </div>

          <label className="fld">
            <span className="flab">
              {edit.escalated ? "Who it was escalated to" : "Why it was not escalated"}
            </span>
            <textarea
              className="finput"
              value={edit.escalated ? edit.escalatedTo : edit.notEscalatedReason}
              onChange={(e) =>
                setEdit(
                  edit.escalated
                    ? { ...edit, escalatedTo: e.target.value }
                    : { ...edit, notEscalatedReason: e.target.value },
                )
              }
            />
          </label>

          {error && <div className="mdl-error">{error}</div>}

          <div className="chips" style={{ marginTop: 14 }}>
            <button className="btn-post" onClick={saveEdit} disabled={busy === "edit"}>
              {busy === "edit" ? "Saving…" : "Save changes"}
            </button>
            <button className="chip" onClick={() => setEdit(null)} disabled={busy === "edit"}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && !edit && !names && (
        <div className="mdl-error noprint" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}

      {names && (
        <div className="inc-editing noprint">
          <div className="mdl-note">
            Anyone on the app named here can open this report. Adding somebody grants
            them access and tells them; removing somebody takes it away. The leader who
            filed it stays on it and cannot be removed.
          </div>

          <div className="fld">
            <span className="flab">Filed by</span>
            <div className="mdl-picked">
              <span className="mdl-pill locked">{i.reporter}</span>
            </div>
          </div>

          <div className="fld">
            <span className="flab">Anyone else on this report</span>
            {names.length > 0 && (
              <div className="mdl-picked">
                {names.map((r) => (
                  <span className="mdl-pill" key={r.key || r.name}>
                    {r.name}
                    {!r.key && <i> · not on the app</i>}
                    <button
                      onClick={() =>
                        setNames((c) =>
                          c.filter((x) => (x.key || x.name) !== (r.key || r.name)),
                        )
                      }
                      aria-label={`Remove ${r.name}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              className="finput"
              value={hunt}
              onChange={(e) => setHunt(e.target.value)}
              placeholder="Start typing a name…"
            />
            {hunt.trim().length > 0 && (
              <div className="mdl-hits">
                {people
                  .filter(
                    (p) =>
                      p.name.toLowerCase().includes(hunt.trim().toLowerCase()) &&
                      !names.some((c) => c.key === p.key),
                  )
                  .slice(0, 6)
                  .map((p) => (
                    <button
                      key={p.key}
                      className="mdl-hit"
                      onClick={() => {
                        setNames((c) => [...c, p]);
                        setHunt("");
                      }}
                    >
                      {p.name}
                      <i>on the app</i>
                    </button>
                  ))}
                <button
                  className="mdl-hit"
                  onClick={() => {
                    setNames((c) => [...c, { key: "", name: hunt.trim() }]);
                    setHunt("");
                  }}
                >
                  Add “{hunt.trim()}”
                  <i>recorded by name only</i>
                </button>
              </div>
            )}
            <span className="fhint">
              Every leader and above is listed here, whether or not they have opened the
              app yet. Someone who is not on the app is recorded by name and gains no
              access — there is no account to give it to.
            </span>
          </div>

          {error && <div className="mdl-error">{error}</div>}

          <div className="chips" style={{ marginTop: 14 }}>
            <button className="btn-post" onClick={saveNames} disabled={busy === "names"}>
              {busy === "names" ? "Saving…" : "Save people"}
            </button>
            <button
              className="chip"
              onClick={() => {
                setNames(null);
                setHunt("");
              }}
              disabled={busy === "names"}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Only rendered on paper — the screen already says all this in the shell. */}
      <div className="inc-printhead">
        <div className="inc-printmark">CLLXTV · THE ROOM</div>
        <div className="inc-printtitle">Incident report</div>
      </div>

      <div className="inc-sheet">
        <div className="inc-topline">
          <div>
            <div className="inc-ref">{i.id}</div>
            <h3 className="inc-cat">{i.category}</h3>
          </div>
          <span className={"inc-status " + (i.resolved ? "res" : i.acknowledged ? "ack" : "open")}>
            {i.resolved ? "Resolved" : i.acknowledged ? "Acknowledged" : "Open"}
          </span>
        </div>

        <dl className="inc-facts">
          <div>
            <dt>Date of incident</dt>
            <dd>{day(i.occurredOn)}</dd>
          </div>
          <div>
            <dt>Filed</dt>
            <dd>{when(i.filedAt)}</dd>
          </div>
          <div>
            <dt>Reported by</dt>
            <dd>{i.reporter}</dd>
          </div>
          <div>
            <dt>Who it happened to</dt>
            <dd>{i.affected || "—"}</dd>
          </div>
          <div>
            <dt>Who it came from</dt>
            <dd>{i.involved || "—"}</dd>
          </div>
          <div>
            <dt>Escalated</dt>
            <dd>{i.escalated ? "Yes" : "No"}</dd>
          </div>
          {i.reporters && i.reporters.length > 1 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <dt>Reporters</dt>
              <dd>
                {i.reporters
                  .map((r) => r.name + (r.key ? "" : " (not on the app)"))
                  .join(" · ")}
              </dd>
            </div>
          )}
        </dl>

        <div className="inc-block">
          <h4>What happened</h4>
          <p className="inc-body">{i.description}</p>
        </div>

        <div className="inc-block">
          <h4>{i.escalated ? "Escalated to" : "Why it was not escalated"}</h4>
          <p className="inc-body">{i.escalated ? i.escalatedTo : i.notEscalatedReason}</p>
        </div>

        {i.attachments > 0 && (
          <div className="inc-block">
            <h4>
              Photos ({i.attachments})
            </h4>
            <div className="inc-photos">
              {Array.from({ length: i.attachments }, (_, n) => (
                <img
                  key={n}
                  src={`/api/incidents/${encodeURIComponent(i.id)}/file/${n}`}
                  alt={`Attachment ${n + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        <div className="inc-stamps">
          <div className="inc-stamp">
            <h4>Acknowledged</h4>
            {i.acknowledged ? (
              <p className="inc-body">
                {i.acknowledged.by} · {when(i.acknowledged.at)}
              </p>
            ) : !senior ? (
              <p className="inc-body muted">Not yet acknowledged.</p>
            ) : (
              <>
                <p className="inc-body muted">Not yet acknowledged.</p>
                <button
                  className="btn-post noprint"
                  onClick={() => stamp("acknowledge")}
                  disabled={busy === "acknowledge"}
                >
                  {busy === "acknowledge" ? "Saving…" : "Acknowledge"}
                </button>
              </>
            )}
          </div>

          <div className="inc-stamp">
            <h4>Resolved</h4>
            {i.resolved ? (
              <p className="inc-body">
                {i.resolved.by} · {when(i.resolved.at)}
              </p>
            ) : !senior ? (
              <p className="inc-body muted">Not yet resolved.</p>
            ) : (
              <>
                <p className="inc-body muted">Not yet resolved.</p>
                <button
                  className="btn-post noprint"
                  onClick={() => stamp("resolve")}
                  disabled={busy === "resolve"}
                >
                  {busy === "resolve" ? "Saving…" : "Mark resolved"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ---- updates ----
            After the stamps because an update is usually what happens *after*
            somebody has looked at it, and part of the printed record because a
            report without its follow-up is only half an account. */}
        <div className="inc-block">
          <h4>
            Updates{(i.updates || []).length > 0 ? ` (${i.updates.length})` : ""}
          </h4>
          {(i.updates || []).length === 0 ? (
            <p className="inc-body muted">
              Nothing added yet. Use this for anything that happens after filing —
              what was followed up, who was spoken to, what was decided.
            </p>
          ) : (
            i.updates.map((u) => (
              <div className="inc-update" key={u.id}>
                <div className="inc-updhead">
                  {u.by} · {when(u.at)}
                </div>
                <p className="inc-body">{u.text}</p>
              </div>
            ))
          )}

          <div className="noprint" style={{ marginTop: 14 }}>
            <textarea
              className="finput"
              style={{ minHeight: 90 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add an update — what happened next, what was followed up, what was decided."
            />
            <div className="chips" style={{ marginTop: 10 }}>
              <button
                className="btn-post"
                onClick={addUpdate}
                disabled={busy === "note" || note.trim().length < 2}
              >
                {busy === "note" ? "Adding…" : "Add update"}
              </button>
            </div>
            <span className="fhint">
              Updates are kept forever and cannot be edited or removed, the same as the
              report itself. Adding one does not clear an acknowledgement — it is new
              information, not a change to what was signed.
            </span>
          </div>
        </div>

        {i.revisions && i.revisions.length > 0 && (
          <div className="inc-block">
            <h4>Edit history ({i.revisions.length})</h4>
            {i.revisions
              .slice()
              .reverse()
              .map((rev, n) => (
                <div className="inc-rev" key={n}>
                  <div className="inc-revhead">
                    {rev.by} · {when(rev.at)}
                    {rev.cleared && rev.cleared.acknowledged && (
                      <em> · cleared an acknowledgement by {rev.cleared.acknowledged.by}</em>
                    )}
                  </div>
                  {rev.fields.map((fl) => (
                    <div className="inc-revline" key={fl.field}>
                      <b>{fl.field}</b>
                      <span className="was">{fl.from || "(blank)"}</span>
                      <span className="now">{fl.to || "(blank)"}</span>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        <div className="inc-foot">
          Confidential · CLLXTV incident record · printed {when(Date.now())}
        </div>
      </div>
    </div>
  );
}

export default function Incidents({ back }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [senior, setSenior] = useState(false);

  const load = () =>
    fetch("/api/incidents")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load reports.");
        setRows(d.incidents || []);
        setSenior(!!d.senior);
      })
      .catch((e) => setError(String(e.message || e)));

  useEffect(() => {
    load();
  }, []);

  const shown = (rows || []).filter((r) => filter === "all" || r.status === filter);

  return (
    <Page
      back={back}
      eyebrow="Confidential"
      title="Incidents."
      intro={
        senior
          ? "Every report filed by a leader."
          : "Reports naming you as a reporter. Others are not listed."
      }
    >
      {error && (
        <div className="card locked">
          <div className="name">
            <span>🔒 {error}</span>
          </div>
        </div>
      )}

      {!error && openId && (
        <Detail id={openId} onBack={() => setOpenId(null)} onChanged={load} senior={senior} />
      )}

      {!error && !openId && (
        <>
          <div className="chips">
            {["all", "open", "acknowledged", "resolved"].map((f) => (
              <button
                key={f}
                className={"chip" + (filter === f ? " on" : "")}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? "All" : STATUS_LABEL[f]}
              </button>
            ))}
          </div>

          {!rows ? (
            <Skeleton />
          ) : rows.length === 0 ? (
            <div className="empty-note">
              {senior
                ? "No reports filed. This fills as leaders file them — it does not backfill anything from before the form existed."
                : "Nothing here. You will see a report once you file one, or once another leader names you as a reporter on theirs."}
            </div>
          ) : (
            <>
              <div className="empty-note">
                {shown.length} of {rows.length} report{rows.length === 1 ? "" : "s"}
              </div>
              {shown.map((r) => (
                <button key={r.id} className="card" onClick={() => setOpenId(r.id)}>
                  <div className="name">
                    <span>{r.category}</span>
                    <span className={"inc-status " + r.status}>{STATUS_LABEL[r.status]}</span>
                  </div>
                  <div className="desc">
                    {day(r.occurredOn)} · filed by {r.reporter} · {r.id}
                    {r.updates > 0 && (
                      <>
                        {" · "}
                        {r.updates} update{r.updates === 1 ? "" : "s"}
                      </>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}
        </>
      )}

      <div className="privacy-note">
        These records name people, including children. Access is gated server-side:
        senior sees every report, and a leader sees only those naming them as a
        reporter. Acknowledging and resolving stay with senior.
      </div>
    </Page>
  );
}
